/**
 * Entitlements helper - derives module access from user_roles + profiles.
 * Single source of truth consumed by client UI and edge functions.
 */

import { supabase } from '@/integrations/supabase/client';

export interface UserEntitlements {
  hasNutrition: boolean;
  hasSleep: boolean;
  hasDreams: boolean;
  hasInflammation: boolean;
  hasCheckIns: boolean;
  hasJournal: boolean;
  hasWhoop: boolean;
  hasSoulGuidance: boolean;
  hasClinicianTools: boolean;
  hasMeditations: boolean;
  hasCourses: boolean;
  tier: string;
}

/**
 * Get entitlements for a user from their roles + profile.
 * No schema changes required - reads existing tables only.
 */
export async function getUserEntitlements(userId: string): Promise<UserEntitlements> {
  const ent: UserEntitlements = {
    hasNutrition: false,
    hasSleep: false,
    hasDreams: false,
    hasInflammation: false,
    hasCheckIns: false,
    hasJournal: false,
    hasWhoop: false,
    hasSoulGuidance: false,
    hasClinicianTools: false,
    hasMeditations: false,
    hasCourses: false,
    tier: 'free',
  };

  try {
    // 1. Get user roles
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    const roleNames = (roles ?? []).map(r => r.role as string);
    const isAdmin = roleNames.includes('admin');
    const isPremium = roleNames.includes('premium') || isAdmin;
    const isClinician = roleNames.includes('clinician');

    // Admin gets everything
    if (isAdmin) {
      return {
        hasNutrition: true, hasSleep: true, hasDreams: true,
        hasInflammation: true, hasCheckIns: true, hasJournal: true,
        hasWhoop: true, hasSoulGuidance: true, hasClinicianTools: true,
        hasMeditations: true, hasCourses: true, tier: 'admin',
      };
    }

    ent.hasClinicianTools = isClinician;

    // 2. Check WHOOP connection via profiles.whoop_user_id
    const { data: profile } = await supabase
      .from('profiles')
      .select('whoop_user_id, school_organization_id')
      .eq('user_id', userId)
      .single();

    ent.hasWhoop = !!(profile?.whoop_user_id);

    // 3. Determine tier
    let tierName = isPremium ? 'Premium' : 'Standard';
    if (profile?.school_organization_id) tierName = 'School License';
    ent.tier = tierName;

    // 4. Map tier to entitlements
    // Standard: therapy chat + check-ins
    ent.hasSoulGuidance = true; // all tiers get Soul Guidance
    ent.hasCheckIns = true;     // all tiers get check-ins

    if (isPremium) {
      ent.hasSleep = true;
      ent.hasDreams = true;
      ent.hasNutrition = true;
      ent.hasJournal = true;
      ent.hasInflammation = true;
      ent.hasMeditations = true;
      ent.hasCourses = true;
    }

    if (tierName === 'School License') {
      ent.hasSleep = true;
      ent.hasDreams = true;
      ent.hasNutrition = true;
      ent.hasJournal = true;
      ent.hasMeditations = true;
    }
  } catch (err) {
    console.error('THERAPPI_FATAL: Failed to fetch entitlements', err);
  }

  return ent;
}
